import { CAPTURE_SCRIPT } from './capture'
import { CHART_SCRIPT } from './chart'
import { PageAccess } from './access'
import { KIT_CSS } from './kit'
import { PageTheme, pageTheme, themeCss } from './theme'

/** A cell's look from the view's colour rules, as the grid draws it. */
export interface CellStyle {
  background: string
  text: string
  bold: boolean
  italic: boolean
  underline: boolean
}

export interface PageColumn {
  id: string
  name: string
  kind: string
  /** The column header's colours in the grid; custom when the owner coloured it (a page then shows them). */
  header: { background: string; text: string; custom: boolean }
  /** Formula, script and joined columns, locked columns, and every column for someone who can't edit. */
  readOnly: boolean
  /** The column must not be blank (a required field, or a no-blanks validation). */
  required: boolean
  /** Select and multiselect columns: every option the grid offers, from a source table and typed in. */
  options?: string[]
  /** Columns whose values narrow `options`; resolve them with morta.options(). */
  optionsDependOn?: string[]
  /** The column's description as plain text, for a form's help text; absent when it has none. */
  description?: string
}

/** What morta.onData gives a page: every row of the view, as the grid holds them, and its columns. */
export interface PageData {
  columns: PageColumn[]
  rows: PageRow[]
  total: number
  /** What this person may do through the page: the view's row settings and their role, as in the grid. */
  access: PageAccess
}

export interface PageRow {
  id: string
  values: Record<string, unknown>
  /** Each value as the grid shows it (the column's decimals, separators, percentages and date format). */
  texts?: Record<string, string>
  /** Cells a colour rule styles (whole-row rules included); absent when none does. */
  styles?: Record<string, CellStyle>
}

/** An attachment's contents, from morta.file(). */
export interface PageFile {
  name: string
  extension: string
  mimetype: string
  size: number
  data: ArrayBuffer
}

/** The libraries a page can use, in the order they go into it: the IFC viewer draws with three.js, so follows it. */
export const PAGE_LIBRARIES = ['d3', 'three', 'ifc'] as const
export type PageLibrary = (typeof PAGE_LIBRARIES)[number]
/** Each library a page uses, as the text of its script (libraries.ts). */
export type PageLibraries = Partial<Record<PageLibrary, string>>

/** What a bound element shows: a cell (column and row) or an aggregate of a column (or a count of rows). */
export const AGGREGATES = ['sum', 'avg', 'count', 'min', 'max'] as const
export type Aggregate = (typeof AGGREGATES)[number]
export interface Binding {
  column?: string
  row?: string
  agg?: Aggregate
}
/** A bound element's attributes as the page wrote them; resolveBindings says why one it can't use is missing. */
export interface RequestedBinding {
  column?: string
  row?: string
  agg?: string
}
export type BoundRaw = string | number | boolean | null
/** A bound element's text, its value, and where it came from; or why it can't be shown. */
export type BoundValue =
  | { text: string; value: BoundRaw; source: { summary: string; filters: string[] } }
  | { missing: string }

/** Most bound elements one page asks about at once. */
export const MAX_BINDINGS = 1000

/** Most rows a single page call may write: the row endpoints' own per-request limit. */
export const MAX_WRITE_ROWS = 2500

/** Most rows one addRows call may add: each goes through the grid's own add row, one at a time. */
export const MAX_ADD_ROWS = 100

/** How long a page waits for a file (a large model can take minutes), and for every other request. */
export const FILE_TIMEOUT_MS = 600_000
const REQUEST_TIMEOUT_MS = 60_000

/**
 * Largest file a page may upload through morta.upload: the most /v1/files stores (MAX_UPLOAD_MB in
 * api/app/services/file.py), so a page is told before the file is sent rather than after.
 */
export const MAX_UPLOAD_BYTES = 35 * 1024 * 1024
const MAX_UPLOAD_NAME = 255

type Params = {
  updateRows: { rows: PageRow[] }
  addRows: { rows: Array<{ values: Record<string, unknown> }> }
  deleteRows: { ids: string[] }
  moveRows: { ids: string[]; before: string }
  previewRow: { values: Record<string, unknown> }
  options: { columnId: string; values: Record<string, unknown> }
  colour: { columnId: string; value: string | number | boolean | null }
  snapshot: Record<string, never>
  bind: { bindings: RequestedBinding[] }
  file: { rowId: string; columnId: string; index: number }
  download: { rowId: string; columnId: string; index: number }
  upload: { columnId: string; name: string; type: string; data: ArrayBuffer }
  ifcWasm: Record<string, never>
}
type BridgeMethod = keyof Params
export type BridgeRequest = {
  [M in BridgeMethod]: { channel: string; id: number; method: M; params: Params[M] }
}[BridgeMethod]

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const hasKeys = (value: Record<string, unknown>, keys: string): boolean => Object.keys(value).sort().join(',') === keys
export const isId = (value: unknown): value is string => typeof value === 'string' && UUID.test(value)
const isIdList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length >= 1 && value.length <= MAX_WRITE_ROWS && value.every(isId)
// A new row may have no values, as a form submitted with nothing filled in adds a blank row; an edit must change one.
const isValues = (value: unknown, allowEmpty: boolean): value is Record<string, unknown> =>
  isRecord(value) && (allowEmpty || Object.keys(value).length > 0) && Object.keys(value).every(isId)
const isRowList = (value: unknown, keys: string, withId: boolean, limit = MAX_WRITE_ROWS): boolean =>
  Array.isArray(value) &&
  value.length >= 1 &&
  value.length <= limit &&
  value.every(
    (row) => isRecord(row) && hasKeys(row, keys) && isValues(row.values, !withId) && (!withId || isId(row.id))
  )

// Only the shape is checked here: a bound element whose column, row or aggregate Morta can't use is answered with
// why (resolveBindings), so one bad element shows a dash rather than failing every other one on the page.
const MAX_BINDING_ATTRIBUTE = 200
const isBinding = (value: unknown): boolean =>
  isRecord(value) &&
  Object.entries(value).every(
    ([key, attribute]) =>
      ['column', 'row', 'agg'].includes(key) &&
      typeof attribute === 'string' &&
      attribute.length <= MAX_BINDING_ATTRIBUTE
  )

// One file in an attachment cell: its row, its column and its place in the cell.
const isAttachmentRef = (p: Record<string, unknown>): boolean =>
  hasKeys(p, 'columnId,index,rowId') &&
  isId(p.rowId) &&
  isId(p.columnId) &&
  Number.isSafeInteger(p.index) &&
  (p.index as number) >= 0 &&
  (p.index as number) < 1000

// A file the page uploads to an attachment column: its name, type and bytes (never a link for Morta to fetch).
const isUpload = (p: Record<string, unknown>): boolean =>
  hasKeys(p, 'columnId,data,name,type') &&
  isId(p.columnId) &&
  typeof p.name === 'string' &&
  p.name.length >= 1 &&
  p.name.length <= MAX_UPLOAD_NAME &&
  typeof p.type === 'string' &&
  p.type.length <= MAX_UPLOAD_NAME &&
  p.data instanceof ArrayBuffer &&
  p.data.byteLength > 0 &&
  p.data.byteLength <= MAX_UPLOAD_BYTES

const PARAM_CHECKS: Record<BridgeMethod, (params: Record<string, unknown>) => boolean> = {
  snapshot: (p) => Object.keys(p).length === 0,
  ifcWasm: (p) => Object.keys(p).length === 0,
  bind: (p) =>
    hasKeys(p, 'bindings') &&
    Array.isArray(p.bindings) &&
    p.bindings.length >= 1 &&
    p.bindings.length <= MAX_BINDINGS &&
    p.bindings.every(isBinding),
  updateRows: (p) => hasKeys(p, 'rows') && isRowList(p.rows, 'id,values', true),
  addRows: (p) => hasKeys(p, 'rows') && isRowList(p.rows, 'values', false, MAX_ADD_ROWS),
  deleteRows: (p) => hasKeys(p, 'ids') && isIdList(p.ids),
  moveRows: (p) => hasKeys(p, 'before,ids') && isIdList(p.ids) && isId(p.before) && !p.ids.includes(p.before),
  // An empty form is a valid preview: it shows defaults and formulas before anything is typed.
  previewRow: (p) => hasKeys(p, 'values') && isRecord(p.values) && Object.keys(p.values).every(isId),
  options: (p) =>
    hasKeys(p, 'columnId,values') && isId(p.columnId) && isRecord(p.values) && Object.keys(p.values).every(isId),
  file: (p) => isAttachmentRef(p),
  download: (p) => isAttachmentRef(p),
  upload: (p) => isUpload(p),
  colour: (p) =>
    hasKeys(p, 'columnId,value') &&
    isId(p.columnId) &&
    (p.value === null || ['string', 'number', 'boolean'].includes(typeof p.value))
}

/** A request from this frame's own channel, whatever its method (the host still checks its params). */
export function isOwnRequest(value: unknown, channel: string): value is { channel: string; id: number } {
  return (
    isRecord(value) &&
    value.channel === channel &&
    Number.isSafeInteger(value.id) &&
    (value.id as number) > 0 &&
    hasKeys(value, 'channel,id,method,params')
  )
}

/** Only the bounded read/write contract is accepted; anything else is refused before any API call. */
export function isBridgeRequest(value: unknown, channel: string): value is BridgeRequest {
  if (!isOwnRequest(value, channel)) return false
  const { method, params } = value as unknown as Record<string, unknown>
  return (
    typeof method === 'string' &&
    Object.prototype.hasOwnProperty.call(PARAM_CHECKS, method) &&
    isRecord(params) &&
    PARAM_CHECKS[method as BridgeMethod](params)
  )
}

/**
 * The page's document, owned by the host: a page is body content (its <style>, markup and <script>) that goes
 * in #morta-root, after the CSP, the bridge, the theme's variables and the base kit, so the page's own styles
 * come last. A page sets its own width (.m-narrow for a document's). No credentials, parent DOM access, or
 * arbitrary API URLs are exposed to page code.
 */
export function buildPageDocument(
  html: string,
  channel: string,
  nonce: string,
  theme: PageTheme = pageTheme(),
  /** The libraries the page's code uses (d3, three.js, the IFC viewer), as scripts (see pageLibraries). */
  libraries: PageLibraries = {}
): string {
  const document = new DOMParser().parseFromString(
    '<!doctype html><html><head></head><body><div id="morta-root"></div></body></html>',
    'text/html'
  )
  // The page's content, all of it into the root: a fragment's leading <style> and <script> parse into a head.
  const page = new DOMParser().parseFromString(html, 'text/html')
  document
    .getElementById('morta-root')!
    .append(...[...page.head.childNodes, ...page.body.childNodes].map((node) => document.importNode(node, true)))
  // These elements are unnecessary for a self-contained page. CSP also applies
  // to dynamically added subresources. This is NOT a JavaScript sanitizer.
  document
    .querySelectorAll('meta,base,link,iframe,frame,frameset,object,embed,script[src]')
    .forEach((node) => node.remove())
  document.querySelectorAll('a,area').forEach((node) => {
    node.removeAttribute('href')
    node.removeAttribute('target')
    node.removeAttribute('download')
  })
  document.querySelectorAll('script').forEach((script) => script.setAttribute('nonce', nonce))
  const policy = document.createElement('meta')
  policy.httpEquiv = 'Content-Security-Policy'
  // web-ifc reads models in WebAssembly, which needs 'wasm-unsafe-eval'; only a page with the IFC viewer gets it.
  const wasm = libraries.ifc ? " 'wasm-unsafe-eval'" : ''
  policy.content = `default-src 'none'; script-src 'nonce-${nonce}'${wasm}; style-src 'unsafe-inline'; img-src data: blob:; connect-src 'none'; font-src data:; media-src blob:; object-src 'none'; frame-src 'none'; worker-src 'none'; base-uri 'none'; form-action 'none'`
  const bootstrap = document.createElement('script')
  bootstrap.setAttribute('nonce', nonce)
  bootstrap.textContent = `(() => {
    const channel = ${JSON.stringify(channel)};
    const nonce = ${JSON.stringify(nonce)};
    let nextId = 0;
    const pending = new Map();
    window.addEventListener('message', (event) => {
      if (event.source !== parent || event.data?.channel !== channel) return;
      // The view's rows changed (an edit, or the reader's filters and sorts): read them again where the page is.
      // A page that has read keeps reading even with no listener for now, so one it adds later gets current rows.
      if (event.data.event === 'change') {
        if (started) refresh(); else start();
        return;
      }
      // A picture of the page, for a document export, once the page has finished loading and drawing.
      if (event.data.event === 'capture') {
        const { id, library, scale } = event.data;
        if (!Number.isSafeInteger(id) || typeof library !== 'string' || !Number.isFinite(scale)) return;
        closeCard();
        capture({ library, scale, nonce, busy: () => pending.size > 0 || refreshing !== null }).then(
          (picture) => parent.postMessage({ channel, event: 'captured', id, ...picture }, '*', [picture.data]),
          (error) => parent.postMessage({ channel, event: 'captured', id, error: String((error && error.message) || error) }, '*')
        );
        return;
      }
      const request = pending.get(event.data.id);
      if (!request) return;
      clearTimeout(request.timer); pending.delete(event.data.id);
      if (event.data.error) request.reject(new Error(event.data.error));
      else request.resolve(event.data.result);
    });
    // Presses inside the page never reach the app's document, where open menus and popovers listen for a click
    // outside them; tell the app, as early as possible (capture phase), so it can close them.
    window.addEventListener('pointerdown', () => parent.postMessage({ channel, event: 'pointerdown' }, '*'), true);
    // A transfer list hands buffers to Morta without copying them (a file's bytes).
    const call = (method, params, label, timeout = ${REQUEST_TIMEOUT_MS}, transfer = []) => new Promise((resolve, reject) => {
      const id = ++nextId;
      const timer = setTimeout(() => { pending.delete(id); reject(new Error(label + ' request timed out')); }, timeout);
      pending.set(id, { resolve, reject, timer });
      parent.postMessage({ channel, id, method, params }, '*', transfer);
    });
    // morta.onData and bound elements: the host reads every row and fills data-morta-* elements, and the page
    // only draws. The document's data-morta-status (loading, ok, empty, error) drives the kit's states.
    const pageTheme = Object.freeze(${JSON.stringify(theme)});
    const dataListeners = new Set();
    const sources = new WeakMap();
    let started = false, refreshing = null, again = false, lastData = null;
    const setStatus = (status) => { document.documentElement.dataset.mortaStatus = status; };
    const emit = (state) => dataListeners.forEach((listener) => { try { listener(state); } catch (error) { console.error(error); } });
    const BOUND = '[data-morta-column], [data-morta-agg]';
    const bind = async (root) => {
      const scope = root || document;
      // The element itself counts too, so morta.bind(element) binds an element drawn on its own.
      const elements = Array.from(scope.querySelectorAll(BOUND));
      if (scope.matches && scope.matches(BOUND)) elements.unshift(scope);
      if (!elements.length) return;
      const bindings = elements.map((element) => {
        const binding = {};
        if (element.dataset.mortaColumn) binding.column = element.dataset.mortaColumn;
        if (element.dataset.mortaRow) binding.row = element.dataset.mortaRow;
        if (element.dataset.mortaAgg) binding.agg = element.dataset.mortaAgg;
        return binding;
      });
      const results = await call('bind', { bindings }, 'Bind');
      elements.forEach((element, index) => {
        const result = results[index];
        if (result.missing) {
          element.textContent = '\u2014';
          element.dataset.mortaMissing = result.missing;
          element.removeAttribute('data-morta-bound');
          sources.delete(element);
        } else {
          element.textContent = result.text;
          element.dataset.mortaBound = '';
          element.removeAttribute('data-morta-missing');
          sources.set(element, result.source);
        }
      });
    };
    const refresh = () => {
      started = true;
      if (refreshing) { again = true; return refreshing; }
      refreshing = (async () => {
        do {
          again = false;
          // Only the first load shows as loading: later refreshes keep what's on the page until they're done.
          if (!lastData) { setStatus('loading'); emit({ status: 'loading', width: window.innerWidth }); }
          try {
            const data = await call('snapshot', {}, 'Data');
            await bind();
            lastData = data;
            setStatus(data.total ? 'ok' : 'empty');
            emit(Object.assign({ status: 'ok', width: window.innerWidth }, data));
          } catch (error) {
            setStatus('error');
            document.querySelectorAll('[data-morta-error-message]').forEach((element) => { element.textContent = error.message; });
            emit({ status: 'error', error: error.message, width: window.innerWidth });
          }
        } while (again);
        refreshing = null;
      })();
      return refreshing;
    };
    const start = () => { if (dataListeners.size || document.querySelector(BOUND)) refresh(); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else setTimeout(start);
    // A new width calls onData again with the same rows, so charts can redraw to fit.
    let lastWidth = window.innerWidth, resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth === lastWidth || !lastData) return;
        lastWidth = window.innerWidth;
        emit(Object.assign({ status: 'ok', width: lastWidth }, lastData));
      }, 150);
    });
    // Where a bound number came from: what it adds up, over how many rows, and the filters applied.
    let card = null;
    const closeCard = () => { if (card) { card.remove(); card = null; } };
    // A dash, the same way: why Morta couldn't show it.
    document.addEventListener('click', (event) => {
      const element = event.target instanceof Element ? event.target.closest('[data-morta-bound], [data-morta-missing]') : null;
      closeCard();
      if (!element) return;
      const source = sources.get(element);
      if (!source && !element.dataset.mortaMissing) return;
      card = document.createElement('div');
      card.className = 'm-tooltip';
      card.setAttribute('role', 'status');
      const summary = document.createElement('strong');
      summary.textContent = source ? source.summary : "Morta can't show this value";
      card.append(summary);
      const detail = document.createElement('div');
      if (source) detail.textContent = source.filters.length ? 'Filtered: ' + source.filters.join('; ') : 'No filters';
      else detail.textContent = element.dataset.mortaMissing;
      card.append(detail);
      const box = element.getBoundingClientRect();
      card.style.left = Math.max(8, Math.min(box.left, window.innerWidth - 248)) + 'px';
      card.style.top = (box.bottom + 6) + 'px';
      document.body.append(card);
    });
    window.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeCard(); });
    window.addEventListener('scroll', closeCard, true);
    ${CHART_SCRIPT}
    ${CAPTURE_SCRIPT}
    Object.defineProperty(window, 'morta', { value: Object.freeze({
      version: 1,
      updateRows: (rows) => call('updateRows', { rows }, 'Update'),
      addRows: (rows) => call('addRows', { rows }, 'Add'),
      deleteRows: (ids) => call('deleteRows', { ids }, 'Delete'),
      moveRows: (ids, { before } = {}) => call('moveRows', { ids, before }, 'Move'),
      previewRow: (values = {}) => call('previewRow', { values }, 'Preview'),
      options: (columnId, values = {}) => call('options', { columnId, values }, 'Options'),
      colour: (columnId, value = null) => call('colour', { columnId, value }, 'Colour'),
      // An attachment's contents as {name, extension, mimetype, size, data: ArrayBuffer}, read by Morta.
      file: (rowId, columnId, index = 0) => call('file', { rowId, columnId, index }, 'File', ${FILE_TIMEOUT_MS}),
      // Saves an attachment to the reader's device, through Morta: the sandbox blocks the page's own downloads.
      download: (rowId, columnId, index = 0) => call('download', { rowId, columnId, index }, 'Download', ${FILE_TIMEOUT_MS}),
      // Stores a file the page has (a File or Blob, or {name, type, data: ArrayBuffer}) in an attachment column, as the
      // grid's own upload does, and resolves to the attachment value to save in that column's list with addRows or
      // updateRows. A File is read here; an object's buffer is copied, so the page keeps its own if the upload fails.
      upload: async (columnId, file) => {
        const isBlob = file instanceof Blob;
        if (!file || (!isBlob && !(file.data instanceof ArrayBuffer))) {
          throw new TypeError('morta.upload needs a File or Blob, or {name, type, data: ArrayBuffer}');
        }
        // Checked here as well as by the host, which can only answer a bad request with a generic one.
        const name = file.name || 'upload';
        const size = isBlob ? file.size : file.data.byteLength;
        if (name.length > ${MAX_UPLOAD_NAME}) throw new RangeError('morta.upload: the file name is longer than ${MAX_UPLOAD_NAME} characters');
        if ((file.type || '').length > ${MAX_UPLOAD_NAME}) throw new RangeError('morta.upload: the file type is longer than ${MAX_UPLOAD_NAME} characters');
        if (size === 0) throw new RangeError('morta.upload: the file is empty');
        if (size > ${MAX_UPLOAD_BYTES}) throw new RangeError('morta.upload: the file is larger than the ${MAX_UPLOAD_BYTES / 1024 / 1024} MB limit');
        const data = isBlob ? await file.arrayBuffer() : file.data.slice(0);
        return call('upload', { columnId, name, type: file.type || '', data }, 'Upload', ${FILE_TIMEOUT_MS}, [data]);
      },
      theme: pageTheme,
      chart,
      // Morta's IFC viewer (libraries.ts) draws a model from an IFC file's bytes, e.g. from morta.file. It asks Morta
      // for web-ifc's wasm, which the page can't fetch itself.
      ifc: Object.freeze({
        view: (container, data, options = {}) => window.__mortaIfc
          ? window.__mortaIfc.view(container, data, options, pageTheme, () => call('ifcWasm', {}, 'IFC reader', ${FILE_TIMEOUT_MS}))
          : Promise.reject(new Error("Morta's IFC viewer isn't in this page. Call morta.ifc.view from the page's own script."))
      }),
      onData: (listener) => {
        if (typeof listener !== 'function') throw new TypeError('morta.onData needs a function');
        dataListeners.add(listener);
        if (started) {
          if (lastData) listener(Object.assign({ status: 'ok', width: window.innerWidth }, lastData));
        } else if (document.readyState !== 'loading') refresh();
        return () => dataListeners.delete(listener);
      },
      // Fill bound elements added after the first load, e.g. in rows the page has just drawn.
      bind: (root) => bind(root),
      // Work a document export waits for before taking the page's picture (a model still loading); returns it.
      loading: (work) => loading(work),
      refresh: () => refresh()
    }) });
  })();`
  const style = document.createElement('style')
  // The theme, then the kit.
  style.textContent = themeCss(theme)
  const kit = document.createElement('style')
  kit.textContent = KIT_CSS
  // The libraries the page uses come before it, so its own scripts find window.d3, window.THREE and morta.ifc.
  const scripts = PAGE_LIBRARIES.flatMap((name) => libraries[name] ?? []).map((source) => {
    const script = document.createElement('script')
    script.setAttribute('nonce', nonce)
    script.textContent = source
    return script
  })
  document.head.prepend(policy, bootstrap, ...scripts, style, kit)
  return '<!doctype html>\n' + document.documentElement.outerHTML
}

export const STARTER_PAGE = `<style>
/* Page-specific touches on the base kit (.m-*), which owns the look. */
.page-header { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: var(--morta-gap-md); }
.search { width: 240px; max-width: 100%; }
.m-table-scroll { max-height: 560px; }
.m-table th button { all: unset; box-sizing: border-box; display: inline-flex; align-items: center; gap: 6px; cursor: pointer; }
.m-table th.num button { width: 100%; justify-content: flex-end; }
.m-table th button:hover, .m-table th button[aria-sort] { color: var(--morta-text); }
.m-table th button:focus-visible { outline: 2px solid var(--morta-info); outline-offset: 2px; }
.arrow { opacity: 0.35; font-size: 10px; }
.m-table th button[aria-sort] .arrow { opacity: 1; }
.empty-value { color: var(--morta-muted); }
.m-badge + .m-badge { margin-left: var(--morta-gap-xs); }
.loading { display: grid; gap: var(--morta-gap-lg); padding: var(--morta-pad-lg); }
.pager-row { display: flex; align-items: center; justify-content: space-between; gap: var(--morta-gap-md); padding: var(--morta-pad-sm) var(--morta-pad-lg); border-top: 1px solid var(--morta-border); }
.pager { display: flex; gap: var(--morta-gap-sm); }
</style>
<header class="page-header">
  <div><h1 class="m-title">View overview</h1><p class="m-subtitle">Live data from this Morta view.</p></div>
  <span class="m-hint" id="updated"></span>
</header>
<section class="m-grid" id="kpis">
  <div class="m-card m-kpi"><div class="m-kpi-label">Records</div><div class="m-kpi-value" data-morta-agg="count"></div><div class="m-kpi-note">In this view</div></div>
</section>
<section class="m-card">
  <div class="m-card-header">
    <div><h2 class="m-card-title">Records</h2><p class="m-hint" id="status" role="status">Loading…</p></div>
    <div class="m-field search"><input id="search" type="search" placeholder="Search records" aria-label="Search records"></div>
  </div>
  <div class="m-table-scroll"><table class="m-table"><thead id="head"></thead><tbody id="rows"></tbody></table></div>
  <div id="message"></div>
  <div class="pager-row" id="footer" hidden><span class="m-hint" id="range"></span><div class="pager"><button class="m-btn m-btn-secondary" id="prev">Previous</button><button class="m-btn m-btn-secondary" id="next">Next</button></div></div>
</section>
<script>
const PAGE_SIZE = 50;
const NUMERIC = ['integer', 'float', 'percentage', 'duration'];
const state = { columns: [], rows: [], query: '', sort: null, page: 0 };
const byId = (id) => document.getElementById(id);
const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
};
const isEmpty = (value) => value == null || value === '' || (Array.isArray(value) && value.length === 0);
// Values as the grid shows them (decimal places, percentages, dates), from each row's texts.
const textOf = (row, column) => (row.texts && row.texts[column.id]) || '';

function renderCell(column, row) {
  const value = row.values[column.id];
  const style = row.styles && row.styles[column.id];
  const td = el('td', NUMERIC.includes(column.kind) ? 'num' : '');
  // The view's colour rules, as the grid draws them.
  if (style) {
    td.style.background = style.background; td.style.color = style.text;
    if (style.bold) td.style.fontWeight = '700';
    if (style.italic) td.style.fontStyle = 'italic';
    if (style.underline) td.style.textDecoration = 'underline';
  }
  if (isEmpty(value)) { td.append(el('span', 'empty-value', '—')); return td; }
  if (Array.isArray(value)) { value.forEach((item) => td.append(el('span', 'm-badge', String(item)))); return td; }
  td.textContent = textOf(row, column);
  td.title = td.textContent;
  return td;
}

function compare(a, b) {
  if (isEmpty(a)) return isEmpty(b) ? 0 : 1;
  if (isEmpty(b)) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function visibleRows() {
  const query = state.query.trim().toLowerCase();
  let rows = state.rows;
  if (query) rows = rows.filter((row) => state.columns.some((c) => textOf(row, c).toLowerCase().includes(query)));
  if (state.sort) {
    const { id, direction } = state.sort;
    rows = rows.slice().sort((a, b) => direction * compare(a.values[id], b.values[id]));
  }
  return rows;
}

function renderHead() {
  const tr = el('tr');
  state.columns.forEach((column) => {
    const th = el('th', NUMERIC.includes(column.kind) ? 'num' : '');
    // A plain header, unless the table's owner coloured it.
    if (column.header.custom) { th.style.background = column.header.background; th.style.color = column.header.text; }
    const button = el('button');
    button.type = 'button';
    button.append(el('span', '', column.name));
    const sorted = state.sort && state.sort.id === column.id;
    if (sorted) button.setAttribute('aria-sort', state.sort.direction === 1 ? 'ascending' : 'descending');
    button.append(el('span', 'arrow', sorted && state.sort.direction === -1 ? '▼' : '▲'));
    button.addEventListener('click', () => {
      state.sort = sorted && state.sort.direction === 1 ? { id: column.id, direction: -1 } : sorted ? null : { id: column.id, direction: 1 };
      state.page = 0;
      renderHead(); renderBody();
    });
    th.append(button); tr.append(th);
  });
  byId('head').replaceChildren(tr);
}

function renderBody() {
  const rows = visibleRows();
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  state.page = Math.min(state.page, pages - 1);
  const start = state.page * PAGE_SIZE;
  const body = byId('rows');
  body.replaceChildren();
  rows.slice(start, start + PAGE_SIZE).forEach((row) => {
    const tr = el('tr');
    state.columns.forEach((column) => tr.append(renderCell(column, row)));
    body.append(tr);
  });
  const message = byId('message');
  message.replaceChildren();
  if (!rows.length) {
    const box = el('div', 'm-state');
    box.append(el('strong', '', state.query ? 'No matching records' : 'No records yet'));
    box.append(state.query ? 'Try a different search.' : 'Records in this view will appear here.');
    message.append(box);
  }
  byId('footer').hidden = rows.length <= PAGE_SIZE;
  byId('range').textContent = rows.length ? (start + 1) + '–' + Math.min(start + PAGE_SIZE, rows.length) + ' of ' + rows.length.toLocaleString() : '';
  byId('prev').disabled = state.page === 0;
  byId('next').disabled = state.page >= pages - 1;
}

// A total for each of the first few number columns: bound elements, so Morta works each out over every row
// of the view and formats it as the table does.
function renderKpis() {
  const kpis = byId('kpis');
  kpis.querySelectorAll('.metric').forEach((node) => node.remove());
  state.columns.filter((c) => c.kind === 'integer' || c.kind === 'float').slice(0, 3).forEach((column) => {
    const card = el('div', 'm-card m-kpi metric');
    card.append(el('div', 'm-kpi-label', 'Total ' + column.name));
    const value = el('div', 'm-kpi-value');
    value.dataset.mortaColumn = column.id;
    value.dataset.mortaAgg = 'sum';
    card.append(value, el('div', 'm-kpi-note', 'Across all records'));
    kpis.append(card);
  });
  morta.bind(kpis);
}

morta.onData(({ status, columns, rows, error }) => {
  if (status === 'loading') { byId('status').textContent = 'Loading…'; return; }
  if (status === 'error') {
    byId('rows').replaceChildren();
    byId('status').textContent = 'Unable to load';
    const box = el('div', 'm-state');
    box.dataset.tone = 'error';
    box.append(el('strong', '', 'Unable to load this view'), error);
    byId('message').replaceChildren(box);
    return;
  }
  const columnsChanged = columns.map((c) => c.id).join() !== state.columns.map((c) => c.id).join();
  state.columns = columns;
  state.rows = rows;
  byId('updated').textContent = 'Updated ' + new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (!columns.length) {
    byId('rows').replaceChildren();
    byId('status').textContent = 'No columns chosen';
    const box = el('div', 'm-state');
    box.append(el('strong', '', 'Choose the columns for this view'));
    box.append('Show columns from Hide Columns in the view toolbar, then save the view.');
    byId('message').replaceChildren(box);
    return;
  }
  byId('status').textContent = rows.length.toLocaleString() + ' records · ' + columns.length + ' columns';
  if (columnsChanged) renderKpis();
  renderHead();
  renderBody();
});

byId('search').addEventListener('input', (event) => { state.query = event.target.value; state.page = 0; renderBody(); });
byId('prev').addEventListener('click', () => { state.page--; renderBody(); });
byId('next').addEventListener('click', () => { state.page++; renderBody(); });
</script>`
